/** Minimum length, in pixels, of a swipe. Same value as the swipe of hammerjs, used here before. */
const SWIPE_MIN_DISTANCE = 10;

/** Minimum horizontal speed, in pixels per millisecond, of a swipe. Same value as the swipe of hammerjs. */
const SWIPE_MIN_VELOCITY = 0.3;

/**
 * Calls `onSwipeLeft` or `onSwipeRight` when one finger slides sideways over the element and is lifted fast enough.
 * The rules are the ones of the hammerjs swipe: the movement is more horizontal than vertical, longer than
 * 10 pixels, faster than 0.3 pixels per millisecond on the horizontal axis, and made with a single finger.
 *
 * The element lets the browser handle only vertical panning (`touch-action: pan-y`, as hammerjs did), so a
 * vertical slide still scrolls the page and the browser cancels the swipe.
 *
 * @param element element that receives the swipe
 * @param onSwipeLeft called when the finger moves to the left
 * @param onSwipeRight called when the finger moves to the right
 * @returns function that removes the listeners from the element
 */
export function addHorizontalSwipeListener(
  element: HTMLElement,
  onSwipeLeft: () => void,
  onSwipeRight: () => void
): () => void {
  const activePointers = new Set<number>();
  let start: { x: number; y: number; time: number } = null;
  let usedMoreFingers = false;

  // The end of the touch can happen outside the element, so it is read on the window while a touch is active
  const stopListeningOnWindow = (): void => {
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerCancel);
  };

  const onPointerDown = (event: PointerEvent): void => {
    activePointers.add(event.pointerId);
    if (activePointers.size === 1) {
      start = { x: event.clientX, y: event.clientY, time: event.timeStamp };
      usedMoreFingers = false;
      window.addEventListener("pointerup", onPointerUp);
      window.addEventListener("pointercancel", onPointerCancel);
    } else {
      usedMoreFingers = true;
    }
  };

  const onPointerUp = (event: PointerEvent): void => {
    if (!activePointers.delete(event.pointerId) || activePointers.size > 0) {
      return;
    }
    stopListeningOnWindow();
    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    const elapsed = Math.max(event.timeStamp - start.time, 1);
    const isSwipe =
      !usedMoreFingers &&
      Math.abs(deltaX) >= Math.abs(deltaY) &&
      Math.hypot(deltaX, deltaY) > SWIPE_MIN_DISTANCE &&
      Math.abs(deltaX) / elapsed > SWIPE_MIN_VELOCITY;
    start = null;
    if (isSwipe) {
      if (deltaX < 0) {
        onSwipeLeft();
      } else {
        onSwipeRight();
      }
    }
  };

  const onPointerCancel = (event: PointerEvent): void => {
    activePointers.delete(event.pointerId);
    if (activePointers.size === 0) {
      stopListeningOnWindow();
      start = null;
    }
  };

  element.style.touchAction = "pan-y";
  element.addEventListener("pointerdown", onPointerDown);

  return () => {
    element.removeEventListener("pointerdown", onPointerDown);
    stopListeningOnWindow();
  };
}
